import { useState, useEffect } from "react";
import api from "../../lib/api";
import {
  X,
  CheckCircle2,
  Calendar,
  Clock,
  Phone,
  User,
  Info,
  AlertCircle,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { getUserStatusBadge, getConvertedBadge, getSourceBadge } from "./conversionHelpers";

export default function ConversionDetailModal({
  conversation,
  onClose,
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const convId = conversation?.id;

  useEffect(() => {
    if (!convId) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    api
      .get(`/ai/chat-history/conversations/${convId}/conversion`)
      .then((res) => {
        if (isMounted) {
          if (res.data?.success) {
            setData(res.data);
          } else {
            setData(null);
            setError(res.data?.message || "Failed to load conversion details.");
          }
        }
      })
      .catch((err) => {
        if (isMounted) {
          console.error("Failed to fetch conversion details:", err);
          setError(
            err?.response?.data?.detail ||
            err?.message ||
            "Failed to load conversion details."
          );
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [convId]);

  // Handle ESC key to close
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const user = data?.user || conversation?.user_details || conversation?.customer || {};
  const isNew = data?.is_new || conversation?.is_new;
  const isConverted = (data?.is_converted || conversation?.is_converted) === "Yes";
  const conversionDetails = data?.conversion_details || conversation?.conversion_details;

  const userStatusInfo = getUserStatusBadge(isNew);
  const convertedBadgeInfo = getConvertedBadge(isConverted ? "Yes" : "No");

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-fade-up"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="conversion-detail-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="bg-slate-900 text-white px-5 py-4 flex items-center justify-between border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#6E3667] flex items-center justify-center text-white shrink-0 shadow-xs">
              <Sparkles className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-bold text-base text-white">
                  Conversion Details
                </h3>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/10 text-slate-200 border border-white/15">
                  Conv #{convId}
                </span>
                <span
                  className={cn(
                    "text-xs font-bold px-2.5 py-0.5 rounded-full border",
                    isConverted
                      ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                      : "bg-slate-500/20 text-slate-300 border-slate-500/40"
                  )}
                >
                  {isConverted ? "✓ Converted" : "Not Converted"}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Conversation started at:{" "}
                <span className="text-slate-200 font-medium">
                  {data?.conversation_created_at || conversation?.created_at || "—"}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto space-y-4 text-xs flex-1">
          {loading ? (
            <div className="py-16 text-center text-slate-400">
              <div className="flex flex-col items-center justify-center gap-2.5">
                <RefreshCw className="w-6 h-6 animate-spin text-[#6E3667]" />
                <span className="text-xs font-medium text-slate-600">
                  Fetching conversion details...
                </span>
              </div>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
              <div>
                <div className="font-semibold text-xs">Error Loading Details</div>
                <div className="text-[11px] text-red-600 mt-0.5">{error}</div>
              </div>
            </div>
          ) : (
            <>
              {/* Customer Info Card */}
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-xs">
                      <User className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <div className="font-bold text-slate-800 text-sm leading-tight">
                        {user.name || "Customer / Guest"}
                      </div>
                      <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                        {user.mobile && (
                          <span className="flex items-center gap-1 font-mono">
                            <Phone className="w-3 h-3 text-slate-400" />
                            {user.mobile}
                          </span>
                        )}
                        {user.cuid && (
                          <span className="text-slate-400">CUID: #{user.cuid}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Customer Status Badge */}
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border",
                        userStatusInfo.badgeClass
                      )}
                      title={userStatusInfo.description}
                    >
                      <span className={cn("w-2 h-2 rounded-full", userStatusInfo.dotClass)} />
                      <span>{userStatusInfo.label} Customer</span>
                    </span>

                    <span
                      className={cn(
                        "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border",
                        user.is_guest
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-sky-50 text-sky-700 border-sky-200"
                      )}
                    >
                      {user.is_guest ? "Guest" : "Registered"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Converted Event Banner */}
              {isConverted && conversionDetails ? (
                <div className="bg-gradient-to-br from-emerald-50/90 to-teal-50/50 border border-emerald-200 rounded-xl p-4 space-y-3 shadow-2xs">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>Conversion Recorded Post-Chat</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {conversionDetails.source && (
                        <span
                          className={cn(
                            "px-2.5 py-0.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 shadow-2xs",
                            getSourceBadge(conversionDetails.source_id, conversionDetails.source).badgeClass
                          )}
                        >
                          <span>{getSourceBadge(conversionDetails.source_id, conversionDetails.source).icon}</span>
                          <span>{conversionDetails.source}</span>
                        </span>
                      )}
                      <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-600 text-white shadow-2xs">
                        {conversionDetails.type_label || (conversionDetails.type === "pickup_request" ? "Pickup Request" : "Order")}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-1">
                    <div className="bg-white/80 border border-emerald-100 rounded-lg p-2.5">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Reference ID
                      </div>
                      <div className="font-mono font-bold text-slate-900 mt-0.5 truncate text-xs" title={conversionDetails.id}>
                        {conversionDetails.id || conversionDetails.reference_id}
                      </div>
                    </div>

                    <div className="bg-white/80 border border-emerald-100 rounded-lg p-2.5">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Booking Source
                      </div>
                      <div className="mt-0.5">
                        {conversionDetails.source ? (
                          <span
                            className={cn(
                              "px-2 py-0.5 rounded-full text-[11px] font-semibold inline-flex items-center gap-1",
                              getSourceBadge(conversionDetails.source_id, conversionDetails.source).badgeClass
                            )}
                          >
                            <span>{getSourceBadge(conversionDetails.source_id, conversionDetails.source).icon}</span>
                            <span className="truncate">{conversionDetails.source}</span>
                          </span>
                        ) : (
                          <span className="text-slate-500 text-xs font-medium">Direct / Store</span>
                        )}
                      </div>
                    </div>

                    <div className="bg-white/80 border border-emerald-100 rounded-lg p-2.5">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Operational Status
                      </div>
                      <div className="font-semibold text-emerald-700 mt-0.5 text-xs truncate">
                        {conversionDetails.status || "Assigned"}
                      </div>
                    </div>

                    <div className="bg-white/80 border border-emerald-100 rounded-lg p-2.5">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Amount
                      </div>
                      <div className="font-bold text-slate-900 mt-0.5 text-xs">
                        ₹{Number(conversionDetails.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>

                  {/* Slot & Service Details */}
                  {(conversionDetails.request_date || conversionDetails.time_slot || conversionDetails.services) && (
                    <div className="bg-white/80 border border-emerald-100 rounded-lg p-3 space-y-2">
                      {(conversionDetails.request_date || conversionDetails.time_slot) && (
                        <div className="flex items-center gap-4 text-slate-700 flex-wrap">
                          {conversionDetails.request_date && (
                            <span className="flex items-center gap-1.5 font-medium">
                              <Calendar className="w-3.5 h-3.5 text-emerald-600" />
                              <span>{conversionDetails.request_date}</span>
                            </span>
                          )}
                          {conversionDetails.time_slot && (
                            <span className="flex items-center gap-1.5 font-medium">
                              <Clock className="w-3.5 h-3.5 text-emerald-600" />
                              <span>{conversionDetails.time_slot}</span>
                            </span>
                          )}
                        </div>
                      )}

                      {conversionDetails.services && (
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            Booked Services
                          </div>
                          <div className="text-slate-800 font-medium text-xs mt-0.5">
                            {conversionDetails.services}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="text-[11px] text-emerald-700/90 font-medium flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Placed on {conversionDetails.created_at}</span>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center space-y-2">
                  <div className="w-10 h-10 rounded-full bg-slate-200/80 text-slate-500 mx-auto flex items-center justify-center">
                    <Info className="w-5 h-5 text-slate-500" />
                  </div>
                  <h4 className="font-bold text-slate-800 text-sm">
                    No Direct Conversion Post-Chat
                  </h4>
                  <p className="text-slate-500 text-xs max-w-md mx-auto">
                    No orders or pickup requests were placed by this customer on or after the conversation start time (
                    <span className="font-semibold text-slate-700">
                      {data?.conversation_created_at || conversation?.created_at || "—"}
                    </span>
                    ).
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="bg-slate-50 px-5 py-3 border-t border-slate-200 flex items-center justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-800 hover:bg-slate-900 text-white font-medium rounded-lg text-xs transition-colors shadow-xs cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

